import { Ellipsis } from 'lucide-react';
import modal_open from 'images/icons/company/modal_open.svg';
import close from 'images/icons/orders/close.svg';
import trash_black from 'images/ui_icon/trash_black.svg';
import * as React from 'react';
import { getTranslate } from 'react-localize-redux';
import { FullOpen } from '../../containers_v2/client-companies/style/PopupStyle';
import { ToolbarImageContainer } from '../../containers_v2/globals/defaultToolbar/style/Style';
import { LoadingStateEnum } from '../../containers_v2/import/model';
import { FlexDiv } from '../../containers_v2/products/style';
import storeLang from '../../helpers/storeLang';
import { Loader } from '../../styles/global/css/GlobalLoader';
import { GreySidely3 } from '../../styles/global/css/Utils';
import Dropdown from '../dropdown/Dropdown';
import { DropdownData } from '../dropdown/model/Model';
import { Body, CommentHeaderTitle, Container, CrossClose, Footer, Header, HeaderTitle, ValidateButton } from './style/PopupCreationStyle';

export enum ButtonStyle {
	Default = 0,
	Error = 1,
	White = 2,
	Primary = 3,
	IA = 4
}

export type AdditionalButton = {
	title: React.ReactNode
	onSubmit?: () => void
	canValidate?: boolean
	style?: ButtonStyle
}

type PopupHeaderProps<T = unknown> = {
	title: React.ReactNode,
	onClose?: () => void
	onDelete?: () => void
	headerBackgroundColor?: string
	titleComment?: React.ReactNode,
	onModeChange?: () => void,
	dropdown?: { data: DropdownData<T>[], onChange: (data: DropdownData<T>) => void }
	extended?: boolean,
	trailing?: React.ReactNode
}

export function PopupHeader(props: PopupHeaderProps): JSX.Element {
	return <Header backgroundColor={props.headerBackgroundColor}>
		{props.onClose && <CrossClose src={close} alt="" onClick={() => props.onClose?.()} />}
		<FlexDiv flow='column' align='flex-start' ellipsis width='100%'>
			<HeaderTitle>{props.title}</HeaderTitle>
			{props.titleComment && <CommentHeaderTitle>{props.titleComment}</CommentHeaderTitle>}
		</FlexDiv>
		{props.onDelete && <CrossClose src={trash_black} onClick={props.onDelete} auto />}
		{props.onModeChange && <div style={{ marginLeft: 'auto' }}><FullOpen src={modal_open} isActive={props.extended ?? false} onClick={props.onModeChange} /></div>}
		{props.trailing}
		{props.dropdown && <div style={{ marginLeft: props.onModeChange ? '10px' : 'auto' }}>
			<Dropdown
				searchable={false}
				dropdownStyle={{
					optionLeft: '-170px',
					optionWidth: '200px',
					optionHeight: '200px',
					height: '25px'
				}}
				datalist={props.dropdown.data}
				name={'dropdownOptionEvent'}
				JSXButton={() => <ToolbarImageContainer rotate={props.onModeChange ? '90deg' : undefined} hasPointer><Ellipsis color={GreySidely3} strokeWidth={2.5} size={24} /></ToolbarImageContainer>}
				onChange={props.dropdown.onChange}
			/>
		</div>}
	</Header>;
}

export function AdditionalButtons(props: { buttons?: AdditionalButton[], disabled?: boolean }): JSX.Element {
	return <>
		{props.buttons?.map((ab, i) =>
			<ValidateButton
				key={`additionalButton[${i}]`}
				disabled={ab.canValidate === false || props.disabled}
				onClick={ab.onSubmit}
				buttonStyle={ab.style}
			>
				{ab.title}
			</ValidateButton>
		)}
	</>;
}

function PopupCreation<T = any>(props: PopupHeaderProps<T> & {
	content?: JSX.Element
	children?
	canValidate?: boolean
	onSubmit?: () => void
	loading?: LoadingStateEnum
	hideValidation?: boolean
	messageButton?: string | JSX.Element
	footerMessage?: React.ReactNode
	additionalButton?: AdditionalButton[]
	noHeader?: boolean,
	overflow?: string
}): JSX.Element {
	const translate = getTranslate(storeLang.getState().localize);

	return (
		(<Container>
			{!props.noHeader && <PopupHeader {...props} />}
			<Body hideValidation={props.hideValidation} overflow={props.overflow}>
				{
					props.loading && props.loading === LoadingStateEnum.LOADING
						? <Loader width="25px" center />
						: props.children ?? props.content
				}
			</Body>
			{props.footerMessage}
			{
				!props.hideValidation &&
				<Footer>
					<ValidateButton
						disabled={!props.canValidate || props.loading === LoadingStateEnum.LOADING}
						onClick={props.onSubmit}
					>
						{props.messageButton ?? translate('company.creation.create').toString()}
					</ValidateButton>
					<AdditionalButtons buttons={props.additionalButton} disabled={props.loading === LoadingStateEnum.LOADING} />
				</Footer>
			}
		</Container>)
	);
}

export default PopupCreation;
